import { scrollIntoView } from '@theme/scrolling';
import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {boolean} */
  #openedByUserClick = false;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (!this.details.open) {
      this.#openedByUserClick = true;
      this.#closeSiblings();
    }
  };

  #closeSiblings() {
    const parent = this.parentElement;
    if (!parent?.hasAttribute('data-faqs-close-siblings')) return;

    for (const sibling of parent.querySelectorAll(':scope > accordion-custom > details[open]')) {
      if (sibling instanceof HTMLDetailsElement && sibling !== this.details) {
        AccordionCustom.#animateClose(sibling);
      }
    }
  }

  /**
   * Animates a details element closed by forcing the closing styles while
   * keeping [open], then removing [open] once the transition ends.
   * @param {HTMLDetailsElement} details
   */
  static #animateClose(details) {
    const content = details.querySelector('.details-content');
    if (!content) {
      details.open = false;
      return;
    }

    details.dataset.closing = '';

    const done = () => {
      delete details.dataset.closing;
      content.removeEventListener('transitionend', onEnd);
      details.open = false;
    };

    const onEnd = (/** @type {TransitionEvent} */ e) => {
      if (e.target === content) done();
    };

    content.addEventListener('transitionend', onEnd);
    setTimeout(done, 450);
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }

  #handleToggle = () => {
    if (!this.details.open) {
      this.#openedByUserClick = false;
      return;
    }

    // Only auto-scroll after a shopper click — not after facet morph / default-open.
    const shouldScroll = this.#openedByUserClick;
    this.#openedByUserClick = false;
    if (!shouldScroll) return;

    const scrollContainer = this.closest('.facets-drawer__filters');
    if (!scrollContainer || !(scrollContainer instanceof HTMLElement)) return;

    setTimeout(() => {
      if (!this.details.open) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const elemRect = this.getBoundingClientRect();
      const isFullyVisible = elemRect.top >= containerRect.top && elemRect.bottom <= containerRect.bottom;

      if (isFullyVisible) return;

      scrollIntoView(this.summary, {
        ancestor: scrollContainer,
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'start',
      });
    }, 450);
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
